import { Input } from "@legal-500/legal500-design-system";
import type { FieldValues, Path, UseFormRegister } from "react-hook-form";
import { BackgroundField } from "./blockEditorParts";
import {
  BLOCK_BACKGROUNDS,
  type BlockBackground,
  type BlockContentErrors,
  type SectionContent,
} from "./types";
import styles from "../CMS.module.css";

export type SectionFormValues = {
  heading: string;
  background: BlockBackground;
  anchorId: string;
};

type SectionFieldsProps<T extends SectionFormValues & FieldValues> = {
  register: UseFormRegister<T>;
  idPrefix: string;
  errors: BlockContentErrors;
  headingLabel?: string;
  showBackground?: boolean;
};

export function SectionFields<T extends SectionFormValues & FieldValues>({
  register,
  idPrefix,
  errors,
  headingLabel = "Heading (optional)",
  showBackground = true,
}: SectionFieldsProps<T>) {
  return (
    <fieldset className={styles.fieldset}>
      <legend className={styles.fieldsetLegend}>Section</legend>

      <div className={styles.formField}>
        <label className={styles.formLabel} htmlFor={`${idPrefix}-heading`}>
          {headingLabel}
        </label>
        <Input id={`${idPrefix}-heading`} {...register("heading" as Path<T>)} />
      </div>

      {showBackground && (
        <BackgroundField
          register={register}
          name={"background" as Path<T>}
          options={BLOCK_BACKGROUNDS}
        />
      )}

      <div className={styles.formField}>
        <label className={styles.formLabel} htmlFor={`${idPrefix}-anchor-id`}>
          Anchor id (optional)
        </label>
        <Input
          id={`${idPrefix}-anchor-id`}
          helperText="Lowercase letters, numbers and hyphens. Link to it with #anchor-id."
          error={errors.anchorId}
          {...register("anchorId" as Path<T>)}
        />
      </div>
    </fieldset>
  );
}

export function toSectionFormValues(section: SectionContent): SectionFormValues {
  return {
    heading: section.heading ?? "",
    background: section.background ?? "default",
    anchorId: section.anchorId ?? "",
  };
}
